<template>
  <div v-if="visible" class="open-in-app-banner">
    <div class="open-in-app-banner__icon">
      <i class="uil-mobile-android"></i>
    </div>

    <div class="open-in-app-banner__content">
      <span class="open-in-app-banner__title">{{ $t("mobile_app.banner_title") }}</span>
      <span class="open-in-app-banner__description">{{ $t("mobile_app.banner_description") }}</span>
    </div>

    <button class="open-in-app-banner__action" type="button" @click="openInApp">
      {{ $t("mobile_app.banner_action") }}
    </button>

    <button
      :aria-label="$t('general.close')"
      class="open-in-app-banner__close"
      type="button"
      @click="dismiss"
    >
      <i class="uil-times"></i>
    </button>
  </div>
</template>

<script>
import bridge from "@/core/services/bridge.service";
import {
  AppLinks,
  buildAndroidIntentLink,
  buildAppDeepLink,
  detectMobilePlatform,
} from "@/core/config/app-links";

const DISMISS_KEY = "open_in_app_dismissed_at";
/** Kapatıldıktan sonra banner'ın tekrar gösterilmesi için beklenecek süre. */
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
/** Kapatma hafızası: false yapılırsa banner her sayfa yüklemesinde yeniden görünür (test). */
const REMEMBER_DISMISS = true;
/** iOS'ta uygulama açılmazsa App Store'a düşme süresi. */
const STORE_FALLBACK_MS = 1500;

export default {
  name: "OpenInAppBanner",
  data() {
    return {
      visible: false,
      platform: null,
      storeFallbackTimer: null,
    };
  },
  mounted() {
    // Uygulamanın WebView'ı içindeyken banner gösterilmez.
    if (bridge.isNative) {
      return;
    }

    this.platform = detectMobilePlatform();

    if (!this.platform || this.isDismissed()) {
      return;
    }

    this.visible = true;
  },
  beforeDestroy() {
    this.clearStoreFallback();
    window.removeEventListener("pagehide", this.clearStoreFallback);
  },
  methods: {
    isDismissed() {
      if (!REMEMBER_DISMISS) return false;

      try {
        const dismissedAt = Number(window.localStorage.getItem(DISMISS_KEY));
        return (
          Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS
        );
      } catch (e) {
        return false;
      }
    },
    dismiss() {
      if (REMEMBER_DISMISS) {
        try {
          window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch (e) {
          // Private mode: kapatma kalıcı olmasa da banner gizlenir.
        }
      }

      this.visible = false;
    },
    clearStoreFallback() {
      if (this.storeFallbackTimer) {
        window.clearTimeout(this.storeFallbackTimer);
        this.storeFallbackTimer = null;
      }
    },
    openInApp() {
      const target = window.location.href;

      if (this.platform === "android") {
        // intent:// kurulu değilse S.browser_fallback_url ile Play Store'a gider.
        window.location.href = buildAndroidIntentLink(target);
        return;
      }

      // iOS: scheme denenir, uygulama yoksa sayfa görünür kaldığı için App Store'a geçilir.
      window.addEventListener("pagehide", this.clearStoreFallback, {
        once: true,
      });

      this.storeFallbackTimer = window.setTimeout(() => {
        this.storeFallbackTimer = null;

        if (document.hidden) {
          return;
        }

        window.location.href = AppLinks.ios.storeUrl;
      }, STORE_FALLBACK_MS);

      window.location.href = buildAppDeepLink(target);
    },
  },
};
</script>

<style lang="scss" scoped>
.open-in-app-banner {
  position: fixed;
  right: 16px;
  bottom: 16px;
  left: 16px;
  z-index: 1050;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: #ffffff;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    background-color: rgba(102, 108, 255, 0.1);
    border-radius: 10px;

    i {
      font-size: 22px;
      color: #666cff;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1 1 auto;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
  }

  &__description {
    font-size: 12px;
    color: #6b7280;
  }

  &__action {
    flex: 0 0 auto;
    min-height: 48px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    background-color: #666cff;
    border: 0;
    border-radius: 10px;

    &:active {
      transform: scale(0.98);
    }
  }

  &__close {
    position: absolute;
    top: -10px;
    right: -6px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    color: #6b7280;
    background-color: transparent;
    border: 0;

    i {
      font-size: 18px;
    }
  }
}
</style>
